import {
  ENTITY_FETCH_SUCCESS,
  ENTITY_ADD_SINGLE,
  ENTITY_FILTERS_CHANGED,
  ENTITY_MAP_LOAD_OUTSIDE_VIEWPORT,
  ENTITY_SET_INITIAL_BOUNDS,
  MAP_FILTERS_CHANGED,
  MAP_SET_MAP_FILTER_RADIO,
  MAP_SET_DRAWING_PROPS,
  RESET_MAP_FILTERS,
  ENTITY_LOADING,
  ENTITY_FETCH_COMPLETE
} from '@constants/action-types';
import {
  FETCH_CONFIG_SUCCESS,
  FETCH_CONFIG_UPDATE_SUCCESS
} from '@components/config/constants';
import initialState from '../store/initial-state';

// Returns a new 'types' object (the object in the store which
// contains all entities and their filters and attributes),
// calling the specified 'setter' function to set the
// new attributes on the entity.
const typesSetter = (state, setter, entityTypeNames = []) => {
  const types = {};
  entityTypeNames.forEach(key => {
    types[key] = {
      ...(state.types[key] || {}),
      ...setter(key)
    };
  });
  return types;
};

const resetTypes = (state, queryViewport, entityTypeNames = []) => typesSetter(state, () => ({
  list: [], next: '', queryViewport, inViewport: true
}), entityTypeNames);

const entitiesReducer = (state = initialState.entities, action) => {
  switch (action.type) {
  case FETCH_CONFIG_SUCCESS:
  case FETCH_CONFIG_UPDATE_SUCCESS: {
    const { config: {entities} } = action;
    return {
      ...state,
      types: resetTypes(state, null, entities)
    };
  }
  case ENTITY_SET_INITIAL_BOUNDS: {
    const { viewport } = action;
    const types = {};
    Object.keys(state.types).forEach(type => {
      types[type] = {
        ...(state.types[type] || {}),
        queryViewport: viewport,
        inViewport: true
      };
    });
    return {
      ...state,
      types: {
        ...state.types,
        ...types
      }
    };
  }
  case MAP_SET_DRAWING_PROPS: {
    const { props: { shape } } = action;
    if (shape) {
      // When a shape is set, clear the list of entities,
      // since they'll be loaded when the new geometry filter is set.
      return { ...state, types: resetTypes(state, null, Object.keys(state.types)) };
    }
    return state;
  }
  case MAP_SET_MAP_FILTER_RADIO: {
    const { entityType, viewport } = action;
    const currentEntityState = state.types[entityType] || {};
    return {
      ...state,
      types: {
        ...state.types,
        [entityType]: {
          ...currentEntityState,
          list: [],
          next: '',
          notifyEmpty: true,
          queryViewport: viewport,
          inViewport: true
        }
      }
    };
  }
  case ENTITY_FILTERS_CHANGED: {
    const { entityType, filters, viewport } = action;
    const newFilters = {
      ...((state.types[entityType] || {}).filters),
      ...filters
    };
    Object.keys(filters).forEach(key => {
      if (filters[key] === 'clear') {
        newFilters[key] = false;
      }
    });
    return {
      ...state,
      types: {
        ...state.types,
        [entityType]: {
          ...state.types[entityType],
          filters: { ...newFilters },
          list: [],
          next: '',
          notifyEmpty: true,
          queryViewport: viewport,
          inViewport: true
        }
      }
    };
  }
  case ENTITY_LOADING: {
    const { entityType } = action;
    return {
      ...state,
      types: {
        ...state.types,
        [entityType]: {
          ...state.types[entityType],
          pendingLoad: true
        }
      }
    };
  }
  case ENTITY_ADD_SINGLE: {
    const { entityType } = action;
    const entityState = state.types[entityType] || {};
    const existingList = entityState.list || [];
    const existingEntity = existingList.find(entity => entity.id === action.entity.id);
    // Preserve the highlighted property from the existing entity if it exists
    const mergedEntity = existingEntity && existingEntity.highlighted !== undefined  // eslint-disable-line no-undefined
      ? { ...action.entity, highlighted: existingEntity.highlighted }
      : action.entity;
    const newList = [...existingList.filter(entity => entity.id !== action.entity.id), mergedEntity];
    return {
      ...state,
      types: {
        ...state.types,
        [entityType]: {
          ...entityState,
          list: newList
        }
      }
    };
  }
  case ENTITY_FETCH_SUCCESS: {
    const { entities, next, url, entityType } = action;
    const entityState = state.types[entityType] || {};
    if (!entityState.next || entityState.next === url) {
      const newIDs = new Set();
      entities.forEach(entity => {
        newIDs.add(entity.id);
      });
      const newNext = next ? next : entityState.next || '';
      const existingList = entityState.list || [];
      const newList = [...existingList.filter(entity => !newIDs.has(entity.id)), ...entities];
      return {
        ...state,
        types: {
          ...state.types,
          [entityType]: {
            ...entityState,
            list: newList,
            next: newNext,
            notifyEmpty: false
          }
        }
      };
    }
    return state;
  }
  case ENTITY_FETCH_COMPLETE: {
    const { entityType } = action;
    return {
      ...state,
      types: {
        ...state.types,
        [entityType]: {
          ...state.types[entityType],
          pendingLoad: false
        }
      }
    };
  }
  case ENTITY_MAP_LOAD_OUTSIDE_VIEWPORT: {
    const {entityType} = action;
    return {
      ...state,
      types: {
        ...state.types,
        [entityType]: { ...state.types[entityType], inViewport: false }
      }
    };
  }
  case MAP_FILTERS_CHANGED:{
    const { viewport } = action;
    return {
      ...state,
      types: resetTypes(state, viewport, Object.keys(state.types))
    };
  }
  case RESET_MAP_FILTERS: {
    const { viewport } = action;
    const types = resetTypes(state, viewport, Object.keys(state.types));
    Object.keys(types).forEach(type => {
      const { filters: _filters, ...filtersRemoved } = types[type] || {}; // eslint-disable-line no-unused-vars
      types[type] = filtersRemoved;
    });
    return { ...state, types };
  }
  default:
    return state;
  }
};

export default entitiesReducer;
